import { Inject, Injectable } from "@angular/core";
import { __ } from "@app/shared/functions/object.functions";
import { Tenant } from "@app/shared/models/classes/Tenant";
import { ITenantService } from "@app/shared/services/itenant.service";
import { HypecastOAuthConfig } from "../models/hypecast-oauth-config";
import { HYPECAST_OAUTH_CONFIG } from "../tokens/hypecast-oauth-config.token";
import { HypecastAuthenticationService } from "./authentication.service";
import { IHypecastOAuthService } from "@app/shared/authentication/services/ihypecast-oauth.service";
import { IOAuthService } from "@app/shared/authentication/oauth/ioauth-service";
import { removeIssFromBrowserUrl } from "@app/shared/authentication/oauth/strip-iss.functions";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class HypecastOAuthService implements IHypecastOAuthService {
  constructor(
    private oauthService: IOAuthService,
    @Inject(HYPECAST_OAUTH_CONFIG) private oAuthConfig: HypecastOAuthConfig,
    private tenantService: ITenantService,
    private authenticationService: HypecastAuthenticationService
  ) {
    this.oauthService.configure(this.oAuthConfig);
    this.initialize();
  }

  private applyClientId(clientId?: string): boolean {
    if (!clientId) {
      return false;
    }

    this.oAuthConfig.clientId = clientId;
    this.oauthService.clientId = clientId;

    return true;
  }

  private ensureClientIdConfigured(): Promise<void> {
    if (this.applyClientId(this.oauthService.clientId) || this.applyClientId(this.oAuthConfig.clientId)) {
      return Promise.resolve();
    }

    const currentTenant = this.tenantService.getTenant();
    if (this.applyClientId(currentTenant?.clientId)) {
      return Promise.resolve();
    }

    return firstValueFrom(this.tenantService.getTenant$())
      .then((tenant: Tenant) => {
        if (this.applyClientId(tenant?.clientId)) {
          return;
        }

        console.warn("Missing OAuth client_id: tenant configuration did not provide a clientId");
        throw {
          error: "login_required",
          error_description: "missing_client_id",
        };
      });
  }

  getIdentityClaims(): object | undefined {
    return this.oauthService.getIdentityClaims();
  }

  getIdToken(): string {
    return this.oauthService.getIdToken();
  }

  /**
   * Returns the current access_token.
   */
  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  /**
   * Returns the current refresh_token.
   */
  getRefreshToken(): string {
    return this.oauthService.getRefreshToken();
  }

  /**
   * Returns the expiration date of the access_token
   * as milliseconds since 1970.
   */
  getAccessTokenExpiration(): number {
    return this.oauthService.getAccessTokenExpiration();
  }

  /**
   * Returns the expiration date of the id_token
   * as milliseconds since 1970.
   */
  getIdTokenExpiration(): number {
    return this.oauthService.getIdTokenExpiration();
  }

  /**
   * Checkes, whether there is a valid access_token.
   */
  hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * Checks whether there is a valid id_token.
   */
  hasValidIdToken(): boolean {
    return this.oauthService.hasValidIdToken();
  }

  /**
   * Retrieve a saved custom property of the TokenReponse object. Only if predefined in authconfig.
   */

  logOut(): void {
    this.oauthService.logOut();
  }

  login() {
    if (window.location.href.includes("skipAuthentication")) {
      return Promise.resolve();
    }

    if (window.location.href.includes("coyo-plugin")) {
      return Promise.resolve();
    }

    if (window.location.href.includes("staffbase-plugin")) {
      return Promise.resolve();
    }

    if (window.location.href.includes("iframe-plugin")) {
      return Promise.resolve();
    }

    if (window.location.href.includes("unily-plugin")) {
      return Promise.resolve();
    }

    let chain: Promise<any>;

    if (__.IsNullOrUndefined(this.tenantService.getTenant())) {
      chain = firstValueFrom(this.tenantService.getTenant$())
        // Load discovery document
        .then((q: Tenant) => {
          this.applyClientId(q.clientId);
          return this.oauthService.loadDiscoveryDocument();
        });
    } else {
      this.applyClientId(this.tenantService.getTenant()?.clientId);
      chain = this.oauthService.loadDiscoveryDocument();
    }

    return chain
      .then((q: any) => {
        if (!__.IsNullOrUndefinedOrEmpty(this.oauthService.getRefreshToken())) {
          // A refresh token is present, so try to refresh
          return this.refresh();
        }
        // No refresh token is present
        if (window.location.search.indexOf("code=") === -1) {
          // If no code flow has previously been initialized
          this.oauthService.initCodeFlow();
          return Promise.resolve<boolean>(false);
        } else {
          // If the code flow has previously been initialized
          return this.oauthService.tryLogin().finally(() => {
            // The library cleans code/state/session_state from the URL but
            // leaves the RFC 9207 iss parameter behind (HYPE-1188). Run the
            // strip regardless of whether tryLogin() resolves or rejects.
            removeIssFromBrowserUrl();
          });
        }
      })
      .then((q: any) => {
        if (q === false) {
          // The operation has been unsuccessful
          // TODO: What should we do in this case?
          return Promise.reject(false);
        }
        // The operation was successful

        if (this.oauthService.hasValidAccessToken()) {
          // An access token is present
          return Promise.resolve(true);
        }

        // If all fails, try again a refresh
        return this.refresh();
      })
      .then(() => {
        // Make sure the flow has been completed properly
        if (this.oauthService.state && this.oauthService.state !== "undefined" && this.oauthService.state !== "null") {
          let stateUrl = this.oauthService.state;
          if (stateUrl.startsWith("/") === false) {
            stateUrl = decodeURIComponent(stateUrl);
          }
          console.log(`There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`);
        }
      })
      .then(() => {
        // Initialize any tasks from the authentication service
        this.authenticationService.init();
      });
  }

  private initialize(): void {
    if (!this.oauthService.hasValidAccessToken()) {
      // No valid access token is present

      const result = this.login();
      // login() may return undefined for skipped flows (e.g. plugin URLs)
      if (result && typeof result.catch === "function") {
        result.catch((err) => {
          // `false` is used internally as a signal that the login flow could
          // not complete locally (e.g. tryLogin() returned false or refresh
          // failed) and a redirect to the IdP has already been initiated.
          // Anything else is an unexpected error worth surfacing.
          if (err !== false) {
            console.error("Login initialization failed:", err);
          }
        });
      }
    } else {
      // An access token is present, initialize any tasks from the authentication service.
      // This will be skipped if the local storage already contains information
      this.ensureClientIdConfigured()
        .catch((): void => undefined)
        // Load the discovery document so end_session_endpoint (logoutUrl) is
        // populated. Without it, a later logOut() clears storage but returns
        // before redirecting to the IdP (angular-oauth2-oidc guards on an
        // empty logoutUrl), so the user appears to stay logged in.
        .then(() => this.oauthService.loadDiscoveryDocument().catch((): void => undefined))
        .then(() => {
          this.authenticationService.init();
        });
    }
  }

  private refresh(): Promise<boolean> {
    return this.ensureClientIdConfigured()
      .then(() => this.oauthService.refreshToken())
      .then(() => Promise.resolve(true))
      .catch((result: unknown) => {
        // Subset of situations from https://openid.net/specs/openid-connect-core-1_0.html#AuthError
        const errorResponsesRequiringUserInteraction = [
          "interaction_required",
          "token_refresh_error",
          "login_required",
          "account_selection_required",
          "consent_required",
          "invalid_grant",
          "refresh token",
        ];

        const errorResult = result as {
          error?: { error?: string; error_description?: string } | string;
          error_description?: string;
          message?: string;
        };
        const rawErrorCode =
          (typeof errorResult.error === "object" &&
            errorResult.error !== null &&
            typeof errorResult.error.error === "string" &&
            errorResult.error.error) ||
          (typeof errorResult.error === "string" && errorResult.error) ||
          "";
        const rawErrorDescription =
          (typeof errorResult.error === "object" &&
            errorResult.error !== null &&
            typeof errorResult.error.error_description === "string" &&
            errorResult.error.error_description) ||
          (typeof errorResult.error_description === "string" && errorResult.error_description) ||
          (typeof errorResult.message === "string" && errorResult.message) ||
          "";
        const normalizedErrorText = `${rawErrorCode} ${rawErrorDescription}`.toLowerCase();

        if (errorResponsesRequiringUserInteraction.some((q) => normalizedErrorText.includes(q))) {
          // Logout to restart the flow
          this.oauthService.logOut(false);

          // Restart the code flow
          this.oauthService.initCodeFlow();

          console.warn("User interaction is needed to log in, we will wait for the user to manually log in.");
          return Promise.resolve(false);
        }

        // An unknown error occured
        // TODO: What should we do?
        return Promise.reject(false);
      });
  }
}
